/**
 * Общие форматтеры и словари для админ-панели.
 * Держим их в одном месте, чтобы таблицы, графики и карточки показывали
 * одинаковые подписи, статусы и время.
 */

const dateTimeFormatter = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const dateFormatter = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const shortTimeFormatter = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

const numberFormatter = new Intl.NumberFormat('ru-RU');

const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : dateTimeFormatter.format(date);
}

export function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : dateFormatter.format(date);
}

export function formatShortDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : shortTimeFormatter.format(date);
}

export function formatNumber(value) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return numberFormatter.format(Number(value));
}

/**
 * «5 мин назад», «вчера», «3 дн назад», для старых дат — обычная дата.
 * Используется в списках событий и таблицах, где важна свежесть, а не точное время.
 */
export function formatRelativeTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  const diffSec = Math.round((Date.now() - date.getTime()) / 1000);
  if (diffSec < 0) return 'только что';
  if (diffSec < 45) return 'только что';
  if (diffSec < HOUR) return `${Math.max(1, Math.floor(diffSec / MINUTE))} мин назад`;
  if (diffSec < DAY) return `${Math.floor(diffSec / HOUR)} ч назад`;
  if (diffSec < 2 * DAY) return 'вчера';
  if (diffSec < 7 * DAY) return `${Math.floor(diffSec / DAY)} дн назад`;
  return formatDate(date);
}

/** Длительность в секундах → «45 сек», «12 мин», «2 ч 05 мин». */
export function formatDuration(sec, emptyValue = '—') {
  if (sec == null || sec === '' || Number.isNaN(Number(sec))) return emptyValue;
  const total = Math.max(0, Math.round(Number(sec)));
  if (total === 0) return emptyValue;
  if (total < MINUTE) return `${total} сек`;
  const minutes = Math.round(total / MINUTE);
  if (minutes < 60) return `${minutes} мин`;
  const hours = Math.floor(minutes / 60);
  const restMinutes = minutes % 60;
  return restMinutes ? `${hours} ч ${String(restMinutes).padStart(2, '0')} мин` : `${hours} ч`;
}

export const ROLE_META = {
  admin: { label: 'Админ', color: 'gold' },
  manager: { label: 'Руководитель', color: 'blue' },
  user: { label: 'Пользователь', color: 'default' },
};

export function roleMeta(role) {
  return ROLE_META[role] || { label: role || '—', color: 'default' };
}

const STATUS_META = {
  pending: { label: 'В очереди', color: 'default' },
  processing: { label: 'В обработке', color: 'processing' },
  in_progress: { label: 'В обработке', color: 'processing' },
  running: { label: 'В обработке', color: 'processing' },
  completed: { label: 'Готово', color: 'success' },
  success: { label: 'Готово', color: 'success' },
  done: { label: 'Готово', color: 'success' },
  error: { label: 'Ошибка', color: 'error' },
  failed: { label: 'Ошибка', color: 'error' },
  failure: { label: 'Ошибка', color: 'error' },
  cancelled: { label: 'Отменено', color: 'warning' },
  canceled: { label: 'Отменено', color: 'warning' },
};

export function statusMeta(status) {
  const key = String(status || '').toLowerCase();
  return STATUS_META[key] || { label: status || '—', color: 'default' };
}

const AUDIT_EVENT_META = {
  login: { label: 'Вход в систему', color: 'blue' },
  user_registered: { label: 'Регистрация', color: 'geekblue' },
  socket_connect: { label: 'Подключение', color: 'default' },
  socket_disconnect: { label: 'Отключение', color: 'default' },
  task_created: { label: 'Создан поиск', color: 'green' },
  task_deleted: { label: 'Удалён поиск', color: 'red' },
  admin_user_created: { label: 'Создан пользователь', color: 'gold' },
  admin_user_updated: { label: 'Изменён пользователь', color: 'gold' },
  admin_password_changed: { label: 'Смена пароля', color: 'orange' },
  admin_user_deleted: { label: 'Удалён пользователь', color: 'red' },
  admin_user_deactivated: { label: 'Блокировка пользователя', color: 'volcano' },
};

export function auditEventMeta(eventType) {
  return (
    AUDIT_EVENT_META[eventType] || {
      label: eventType || 'Событие',
      color: 'default',
    }
  );
}

const CSV_DELIMITER = ';';

function csvCell(value) {
  const text = value == null ? '' : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}

/**
 * Клиентский экспорт в CSV, который корректно открывается в русском Excel:
 * BOM + точка с запятой. columns: [{ title, dataIndex | value(row) }]
 */
export function downloadCsv(filename, columns, rows) {
  const header = columns.map((column) => csvCell(column.title)).join(CSV_DELIMITER);
  const body = (rows || []).map((row) =>
    columns
      .map((column) =>
        csvCell(typeof column.value === 'function' ? column.value(row) : row[column.dataIndex])
      )
      .join(CSV_DELIMITER)
  );
  const content = `\uFEFF${[header, ...body].join('\r\n')}`;
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function todayFilename(prefix) {
  const stamp = new Date().toISOString().slice(0, 10);
  return `${prefix}-${stamp}.csv`;
}

export async function copyText(text) {
  if (!text) return false;
  try {
    await navigator.clipboard.writeText(String(text));
    return true;
  } catch {
    const area = document.createElement('textarea');
    area.value = String(text);
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    return ok;
  }
}
